import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import * as Icons from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading, StatsBand, BrandLogos, BrandLogo, StatusBadge, CtaBand } from "@/components/site/Blocks";
import { IMAGES, PILLARS, PRINCIPLES, HOTELS, EXPERTISE, TAKEOVER } from "@/lib/content";

const Hero = () => (
  <section className="relative flex min-h-[100svh] items-end overflow-hidden bg-navy text-ivory" data-testid="home-hero">
    <img src={IMAGES.lobby} alt="Warmly lit modern hotel lobby at dusk" className="hero-zoom absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/55 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-transparent" />
    <div className="container-x relative pb-20 pt-40 md:pb-28">
      <p className="eyebrow hero-in text-gold">Hotel Management · Kansas City Metro</p>
      <h1 className="hero-in mt-6 font-display text-5xl font-medium leading-[0.95] sm:text-6xl lg:text-8xl" style={{ animationDelay: "120ms" }}>
        All In, <em className="text-gold">On Site.</em>
      </h1>
      <p className="hero-in mt-8 max-w-xl text-base leading-relaxed text-ivory/80 md:text-lg" style={{ animationDelay: "260ms" }}>
        Local owners and brand standards. MSK Hotels is an owner-operated management company whose principals live in the metro and run their hotels from the property, not from a distance.
      </p>
      <div className="hero-in mt-10 flex flex-wrap gap-4" style={{ animationDelay: "400ms" }}>
        <Link to="/contact" className="btn-gold" data-testid="hero-contact-button">
          Partner with MSK <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/approach" className="btn-outline-light" data-testid="hero-approach-button">
          Our approach
        </Link>
      </div>
    </div>
  </section>
);

const ValueProp = () => (
  <section className="bg-ivory py-28 md:py-36" data-testid="home-value-prop">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <Reveal className="lg:col-span-7">
        <p className="eyebrow text-gold-dark">Why we exist</p>
        <p className="mt-6 font-display text-3xl font-medium leading-[1.15] text-navy sm:text-4xl lg:text-5xl">
          Capital and systems aren't what limit a hotel today. <span className="italic text-gold-dark">The right team on property is</span>: hired, led and held accountable by people who are there.
        </p>
      </Reveal>
      <div className="space-y-10 lg:col-span-5 lg:pt-14">
        {PRINCIPLES.map((p, i) => (
          <Reveal key={p.key} delay={i * 120} className="border-l border-gold/60 pl-6" data-testid={`principle-${p.key}`}>
            <h3 className="font-display text-2xl text-navy">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 md:text-base">{p.text}</p>
          </Reveal>
        ))}
        <Reveal delay={400}>
          <Link to="/about" className="link-arrow" data-testid="value-prop-about-link">
            Our philosophy <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const PillarsPreview = () => (
  <section className="bg-cream py-28 md:py-36" data-testid="home-pillars-preview">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHeading eyebrow="Operating philosophy" title="Five pillars. One standard for every hotel." intro="The same model runs at every property we own or oversee, from underwriting to the monthly owner report." />
          <Reveal delay={150} className="mt-10 overflow-hidden">
            <img src={IMAGES.guestroom} alt="Refined hotel guest suite in morning light" loading="lazy" decoding="async" className="img-hover aspect-[4/3] w-full object-cover" />
          </Reveal>
        </div>
      </div>
      <ol className="lg:col-span-7">
        {PILLARS.map((p, i) => (
          <Reveal as="li" key={p.n} delay={i * 80} className="pillar-row group grid grid-cols-[auto,1fr] gap-6 border-b border-navy/10 py-10 first:pt-0" data-testid={`pillar-preview-${p.n}`}>
            <span className="font-display text-5xl text-gold-dark/70 transition-colors duration-500 group-hover:text-gold-dark">{p.n}</span>
            <div>
              <h3 className="font-display text-2xl text-navy md:text-3xl">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{p.summary}</p>
            </div>
          </Reveal>
        ))}
        <Reveal className="pt-10">
          <Link to="/approach" className="link-arrow" data-testid="pillars-approach-link">
            Explore our approach <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </ol>
    </div>
  </section>
);

const BrandsSection = () => (
  <section className="bg-ivory py-28 md:py-32" data-testid="home-brands-section">
    <div className="container-x">
      <SectionHeading eyebrow="Brands under management" title="Proud to fly the flags of three great brand families." intro="Marriott, IHG and Choice hotels are owned, co-sponsored or proposed for management by MSK principals across the Kansas City metro." />
      <div className="mt-16">
        <BrandLogos />
      </div>
    </div>
  </section>
);

const PortfolioPreview = () => (
  <section className="bg-cream py-28 md:py-36" data-testid="home-portfolio-preview">
    <div className="container-x">
      <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <SectionHeading eyebrow="Portfolio" title="Five hotels. 437 keys. One metro." intro="Every property sits within a short drive of the people accountable for it." />
        <Reveal>
          <Link to="/portfolio" className="link-arrow" data-testid="home-portfolio-link">
            View the portfolio <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        {HOTELS.map((h, i) => (
          <Reveal key={h.id} delay={i * 80}>
            <Link to="/portfolio" className="hotel-card group flex h-full flex-col bg-ivory p-6" data-testid={`home-hotel-card-${h.id}`}>
              <div className="flex h-16 items-center">
                <BrandLogo src={h.logo} name={h.brand} h={h.logoH} />
              </div>
              <h3 className="mt-6 font-display text-xl leading-snug text-navy">{h.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{h.city}</p>
              <div className="mt-auto flex flex-col items-start gap-3 pt-8">
                <StatusBadge status={h.status} />
                <p className="font-display text-3xl text-navy">{h.keys}<span className="ml-1 text-xs uppercase tracking-[0.18em] text-slate-500">keys</span></p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Expertise = () => (
  <section className="bg-ivory py-28 md:py-36" data-testid="home-expertise">
    <div className="container-x">
      <SectionHeading eyebrow="Expertise · For brand reviewers" title="What we run, every day, to brand standard." intro="These are the capabilities a brand approval team looks for. We already run each one as a standing discipline." />
      <div className="mt-16 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
        {EXPERTISE.map((x, i) => {
          const Icon = Icons[x.icon];
          return (
            <Reveal key={x.title} delay={(i % 4) * 90} className="dept-card group bg-ivory p-8" data-testid={`expertise-${i + 1}`}>
              <Icon className="h-7 w-7 text-gold-dark transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.3} />
              <h3 className="mt-6 font-display text-2xl leading-tight text-navy">{x.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{x.text}</p>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-10">
        <Link to="/operations#command-center" className="cc-teaser group grid items-center gap-8 bg-navy p-8 text-ivory md:grid-cols-12 md:p-10" data-testid="home-command-center-teaser">
          <div className="md:col-span-7">
            <p className="eyebrow text-gold">MSK Command Center</p>
            <p className="mt-4 font-display text-3xl leading-tight md:text-4xl">Our own operating platform: review, decide, act and verify across every property, every day.</p>
            <span className="link-arrow mt-6 !text-ivory">See how it works <ArrowUpRight className="h-4 w-4" /></span>
          </div>
          <div className="hidden md:col-span-5 md:block">
            <div className="grid grid-cols-2 gap-2" aria-hidden="true">
              {["Morning briefing", "Owner intelligence", "Work board", "Performance trends"].map((t, i) => (
                <div key={t} className="border border-white/10 p-3 transition-colors duration-500 group-hover:border-gold/40">
                  <p className="text-[0.55rem] uppercase tracking-[0.2em] text-gold">{String(i + 1).padStart(2, "0")} · {t}</p>
                  <span className="mt-3 block h-1.5 w-4/5 rounded-full bg-white/15" />
                  <span className="mt-1.5 block h-1.5 w-3/5 rounded-full bg-white/10" />
                </div>
              ))}
            </div>
          </div>
        </Link>
      </Reveal>
    </div>
  </section>
);

const Takeover = () => (
  <section className="bg-navy py-28 text-ivory grain md:py-36" data-testid="home-takeover">
    <div className="container-x">
      <SectionHeading light eyebrow="How we take over a hotel" title="From diligence to the first owner report." intro="The leadership bench, reporting cadence and capital calendar all start at closing, not months later." />
      <ol className="relative mt-20 grid gap-12 md:grid-cols-4 md:gap-8">
        <span className="absolute left-0 right-0 top-[7px] hidden h-px bg-gold/40 md:block" aria-hidden="true" />
        {TAKEOVER.map((t, i) => (
          <Reveal as="li" key={t.title} delay={i * 130} className="relative" data-testid={`takeover-step-${i + 1}`}>
            <span className="relative block h-3.5 w-3.5 rounded-full border-2 border-gold bg-navy" />
            <p className="eyebrow mt-6 text-gold">{t.phase}</p>
            <h3 className="mt-3 font-display text-3xl">{t.title}</h3>
            <ul className="mt-5 space-y-2.5">
              {t.points.map((p) => (
                <li key={p} className="flex gap-3 text-sm leading-relaxed text-ivory/70">
                  <span className="mt-2 h-1 w-1 flex-none rounded-full bg-gold" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const LibertyTeaser = () => (
  <section className="bg-cream py-28 md:py-36" data-testid="home-liberty-teaser">
    <div className="container-x grid items-center gap-16 lg:grid-cols-12">
      <Reveal className="relative overflow-hidden lg:col-span-6">
        <img src={IMAGES.bathroom} alt="Newly renovated hotel bathroom with walk-in shower" loading="lazy" decoding="async" className="img-hover aspect-square w-full object-cover" />
        <div className="absolute bottom-0 left-0 bg-navy px-8 py-6 text-ivory">
          <p className="font-display text-5xl text-gold">$1.8M</p>
          <p className="mt-1 text-xs uppercase tracking-[0.2em]">Full renovation, funded by ownership</p>
        </div>
      </Reveal>
      <div className="lg:col-span-6">
        <SectionHeading eyebrow="Case study · Liberty, MO" title="We read the guest data, then fixed the product." intro="At Fairfield Inn & Suites Kansas City Liberty, guests praised the people and the breakfast. Their complaints pointed to bathrooms, odors and room condition. That's a capital problem, so capital is where we started." />
        <Reveal delay={200} className="mt-10">
          <Link to="/portfolio#liberty-case-study" className="btn-navy" data-testid="home-case-study-button">
            Read the case study <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo description="MSK Hotels is an owner-operated hotel management company in the Kansas City metro. Local owners, brand standards and a team on property every day. All In, On Site." path="/" />
      <Hero />
      <ValueProp />
      <StatsBand />
      <BrandsSection />
      <Expertise />
      <PillarsPreview />
      <Takeover />
      <PortfolioPreview />
      <LibertyTeaser />
      <CtaBand />
    </>
  );
}
